import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "salesperson",
  target_type: "turnover",
  target_amount: 0,
};

export default function Salespeople() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetPwd2, setResetPwd2] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (sp) => {
    setEditing(sp);
    setForm({
      name: sp.name,
      email: sp.email,
      password: "",
      role: sp.role || "salesperson",
      target_type: sp.target_type || "turnover",
      target_amount: sp.target_amount || 0,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role: form.role,
          target_type: form.target_type,
          target_amount: parseFloat(form.target_amount) || 0,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/salespeople/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        const payload = {
          ...form,
          target_amount: parseFloat(form.target_amount) || 0,
        };
        await api.post("/salespeople", payload);
        toast.success(`${form.role === "admin" ? "Admin" : "User"} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/salespeople/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openReset = (sp) => { setResetTarget(sp); setResetPwd(""); setResetPwd2(""); };

  const submitReset = async () => {
    if (resetPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (resetPwd !== resetPwd2) { toast.error("Passwords do not match"); return; }
    setResetting(true);
    try {
      await api.put(`/salespeople/${resetTarget.id}`, { password: resetPwd });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setResetPwd(""); setResetPwd2("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setResetting(false); }
  };

  return (
    <Shell
      title="Users"
      subtitle="Create and manage users. Both Users and Admins are part of the sales team and have targets."
      actions={
        <Button onClick={openCreate} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="add-salesperson-button">
          <PlusCircle size={16} className="mr-1" /> Add User
        </Button>
      }
    >
      <Card className="border-zinc-200 shadow-none rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="font-bold text-zinc-950">Name</TableHead>
              <TableHead className="font-bold text-zinc-950">Email</TableHead>
              <TableHead className="font-bold text-zinc-950">Role</TableHead>
              <TableHead className="font-bold text-zinc-950">Target type</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Target</TableHead>
              <TableHead className="font-bold text-zinc-950 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-zinc-500">
                  No users yet. Add your first one to get started.
                </TableCell>
              </TableRow>
            )}
            {items.map((sp) => {
              const isYou = sp.id === me?.id;
              return (
                <TableRow key={sp.id} data-testid={`salesperson-row-${sp.id}`}>
                  <TableCell className="font-semibold">
                    {sp.name}
                    {isYou && <span className="ml-2 text-xs text-zinc-500">(you)</span>}
                  </TableCell>
                  <TableCell className="text-zinc-600">{sp.email}</TableCell>
                  <TableCell>
                    {sp.role === "admin" ? (
                      <Badge className="bg-zinc-950 text-white">Admin</Badge>
                    ) : sp.role === "data_entry" ? (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200" variant="outline">Data Entry</Badge>
                    ) : (
                      <Badge variant="outline">User</Badge>
                    )}
                  </TableCell>
                  <TableCell className="capitalize text-zinc-600">
                    {sp.role === "data_entry" ? <span className="text-zinc-400">—</span> : sp.target_type}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {sp.role === "data_entry" ? <span className="text-zinc-400">—</span> : formatINR(sp.target_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openReset(sp)} data-testid={`reset-pwd-${sp.id}`} title="Reset password">
                      <KeyRound size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(sp)} data-testid={`edit-${sp.id}`} title="Edit">
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(sp)}
                      data-testid={`delete-${sp.id}`}
                      disabled={isYou}
                      title={isYou ? "You cannot delete your own account" : "Delete"}
                    >
                      <Trash2 size={14} className={isYou ? "text-zinc-300" : "text-rose-600"} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="salesperson-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
            </div>
            <div>
              <Label className="font-semibold">Email</Label>
              <Input type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
            </div>
            <div>
              <Label className="font-semibold">{editing ? "New password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-password" />
            </div>
            <div>
              <Label className="font-semibold">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salesperson">User (Salesperson)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                  <SelectItem value="data_entry">Data Entry (enter & view invoices only)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-zinc-500 mt-1">
                Admins see and edit everything. Salespeople see only their own data.
                Data-entry users can enter invoices on behalf of others and view them, but cannot see turnover or margin.
              </div>
            </div>
            {form.role !== "data_entry" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">Target type</Label>
                  <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                    <SelectTrigger data-testid="form-target-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="turnover">Turnover</SelectItem>
                      <SelectItem value="margin">Margin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-semibold">Target (₹)</Label>
                  <Input type="number" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} data-testid="form-target-amount" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="form-save-button">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password for {resetTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">New password</Label>
              <Input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-new" />
              <div className="text-xs text-zinc-500 mt-1">Minimum 6 characters.</div>
            </div>
            <div>
              <Label className="font-semibold">Confirm new password</Label>
              <Input type="password" value={resetPwd2} onChange={(e) => setResetPwd2(e.target.value)} data-testid="reset-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button onClick={submitReset} disabled={resetting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="reset-submit">
              {resetting ? "Saving…" : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-salesperson-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the user and their login. Existing invoices
              referencing them will keep their record. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
